/*
 * COI delivery estimate
 * Renders a live "order within X / delivery between A and B with <carrier>" line.
 * All configuration comes from data-attributes set by snippets/coi-payments-shipping.liquid,
 * so the block can be dropped in multiple times with different settings.
 */
(function () {
  'use strict';

  var INTERVAL_MS = 30000;

  function parseRules(text) {
    var rules = {};
    (text || '').split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.charAt(0) === '#') return;
      var parts = line.split(',').map(function (part) {
        return part.trim();
      });
      if (parts.length < 4) return;
      var min = parseInt(parts[2], 10);
      var max = parseInt(parts[3], 10);
      if (isNaN(min) || isNaN(max)) return;
      rules[parts[0].toUpperCase()] = { carrier: parts[1], min: min, max: max };
    });
    return rules;
  }

  function parseList(text) {
    return (text || '')
      .split(/[\s,;\r\n]+/)
      .map(function (code) {
        return code.trim().toUpperCase();
      })
      .filter(Boolean);
  }

  function getConfig(country, rules, euCountries) {
    if (country && rules[country]) return rules[country];
    if (country && euCountries.indexOf(country) > -1 && rules.EU) return rules.EU;
    if (rules['*']) return rules['*'];
    return null;
  }

  function addBusinessDays(date, amount, skipWeekends) {
    var result = new Date(date.getTime());
    var added = 0;
    while (added < amount) {
      result.setDate(result.getDate() + 1);
      if (!skipWeekends || (result.getDay() !== 0 && result.getDay() !== 6)) added++;
    }
    return result;
  }

  function makeFormatter(locale, format) {
    var options;
    if (format === 'day_month') {
      options = { day: 'numeric', month: 'long' };
    } else if (format === 'weekday_day_month_short') {
      options = { weekday: 'short', day: 'numeric', month: 'short' };
    } else if (format === 'day_month_short') {
      options = { day: 'numeric', month: 'short' };
    } else {
      options = { weekday: 'long', day: 'numeric', month: 'long' };
    }
    try {
      return new Intl.DateTimeFormat(locale, options);
    } catch (e) {
      return new Intl.DateTimeFormat('en-GB', options);
    }
  }

  function humanize(ms, words) {
    var totalMinutes = Math.max(0, Math.round(ms / 60000));
    var hours = Math.floor(totalMinutes / 60);
    var minutes = totalMinutes % 60;
    if (hours > 0) {
      var out = hours + ' ' + (hours === 1 ? words.hour : words.hours);
      if (minutes > 0) out += ' ' + minutes + ' ' + (minutes === 1 ? words.minute : words.minutes);
      return out;
    }
    return minutes + ' ' + (minutes === 1 ? words.minute : words.minutes);
  }

  function fill(template, values) {
    return (template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function init(root) {
    if (!root || root.dataset.coiDeliveryReady === 'true') return;
    var target = root.querySelector('[data-coi-delivery-text]');
    if (!target) return;
    root.dataset.coiDeliveryReady = 'true';

    var data = root.dataset;
    var rules = parseRules(data.coiRules);
    var euCountries = parseList(data.coiEuCountries);
    var country = (data.coiCountry || '').toUpperCase();
    var config = getConfig(country, rules, euCountries);
    if (!config) {
      root.setAttribute('hidden', 'hidden');
      return;
    }

    var skipWeekends = data.coiSkipWeekends === 'true';
    var cutoffParts = (data.coiCutoff || '14:00').split(':');
    var cutoffHours = parseInt(cutoffParts[0], 10);
    var cutoffMinutes = parseInt(cutoffParts[1], 10);
    if (isNaN(cutoffHours)) cutoffHours = 14;
    if (isNaN(cutoffMinutes)) cutoffMinutes = 0;

    var formatter = makeFormatter(data.coiLocale || 'en-GB', data.coiDateFormat);

    var words = {
      hour: data.coiWordHour || 'hour',
      hours: data.coiWordHours || 'hours',
      minute: data.coiWordMinute || 'minute',
      minutes: data.coiWordMinutes || 'minutes'
    };

    var templates = {
      orderWithin: data.coiTextOrderWithin || 'Order within [countdown].',
      orderToday: data.coiTextOrderToday || 'Order today.',
      range: data.coiTextRange || 'For delivery between [from] and [to]',
      single: data.coiTextSingle || 'For delivery [date]',
      carrier: data.coiTextCarrier || 'with [carrier].'
    };

    function strong(value) {
      return '<strong>' + value + '</strong>';
    }

    function rangeHtml(base) {
      var from = addBusinessDays(base, config.min, skipWeekends);
      var to = addBusinessDays(base, config.max, skipWeekends);
      var fromLabel = formatter.format(from);
      var toLabel = formatter.format(to);
      if (fromLabel === toLabel) {
        return fill(templates.single, { date: strong(fromLabel) });
      }
      return fill(templates.range, { from: strong(fromLabel), to: strong(toLabel) });
    }

    function carrierHtml() {
      return fill(templates.carrier, {
        carrier: '<span class="coi-td__carrier">' + config.carrier + '</span>'
      });
    }

    function render() {
      var now = new Date();
      var cutoff = new Date(now.getTime());
      cutoff.setHours(cutoffHours, cutoffMinutes, 0, 0);
      var base = new Date(now.getTime());
      base.setHours(0, 0, 0, 0);

      var parts = [];
      if (now < cutoff) {
        parts.push(fill(templates.orderWithin, { countdown: strong(humanize(cutoff - now, words)) }));
      } else {
        base.setDate(base.getDate() + 1);
        parts.push(templates.orderToday);
      }
      parts.push(rangeHtml(base));
      parts.push(carrierHtml());

      target.innerHTML = parts
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    render();
    var timer = setInterval(function () {
      if (!root.isConnected) {
        clearInterval(timer);
        return;
      }
      render();
    }, INTERVAL_MS);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-coi-delivery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-init after theme editor / variant re-renders swap the markup.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    initAll(event.target);
  });
})();
